/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CompassOutlined,
  SolutionOutlined,
  SettingOutlined,
  ReconciliationOutlined,
  UserOutlined,
  IdcardOutlined,
  TruckOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import { useMemo } from 'react'
import { ItemType } from 'antd/es/menu/hooks/useItems'
import { MenuItemType } from 'antd/lib/menu/hooks/useItems'

import { useContractContracts, useRoutesFilters } from '@contexts'
import { PATHS, RoutesFilters } from '@types'

import { StyledMenu } from '../../../components/StyledComponents'
import { initialContractsFiltersState } from '../../../contexts/ContractsContext'
import {
  initialCarriersFiltersState,
  useCarriersContext,
} from '../../../contexts/CarriersFiltersContext'
import {
  initialTrailersFiltersState,
  useTrailersContext,
} from '../../../contexts/TrailersFiltersContext'
import { useVehiclesContext } from '../../../contexts/VehiclesFiltersContext'
import {
  initialSuppliersFiltersState,
  useSuppliersContext,
} from '../../../contexts/SuppliersFiltersContext'
import {
  initialDriversFiltersState,
  useDriversContext,
} from '../../../contexts/DriversFiltersContext'

const ProcurementMenu: () => JSX.Element = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setFilters: setRoutesFilters } = useRoutesFilters()
  const { setFilters: setContractFilters } = useContractContracts()
  const { setFilters: setCarriersFilters } = useCarriersContext()
  const { setFilters: setTrailersFilters } = useTrailersContext()
  const { setFilters: setVehiclesFilters } = useVehiclesContext()
  const { setFilters: setSuppliersFilters } = useSuppliersContext()
  const { setFilters: setDriversFilters } = useDriversContext()

  const clearDriversFiltersOnComponentChange = () => {
    setDriversFilters(initialDriversFiltersState.filters)
  }
  const clearContractFiltersOnComponentChange = () => {
    setContractFilters(initialContractsFiltersState.filters)
  }
  const clearCarriersFiltersOnComponentChange = () => {
    setCarriersFilters(initialCarriersFiltersState.filters)
  }
  const clearSuppliersFiltersOnComponentChange = () => {
    setSuppliersFilters(initialSuppliersFiltersState.filters)
  }
  const clearTrailersFiltersOnComponentChange = () => {
    setTrailersFilters(initialTrailersFiltersState.filters)
  }
  const clearVehiclesFiltersOnComponentChange = () => {
    setVehiclesFilters({ page: 1, limit: 10 })
  }

  const clearRoutesFiltersOnComponentChange = () => {
    setRoutesFilters((prevState: RoutesFilters) => ({
      ...prevState,
      page: 1,
      asnum: undefined,
      originCityCode: undefined,
      originPostCode: undefined,
      originZone: undefined,
      destinationCityCode: undefined,
      destinationPostCode: undefined,
      destinationZone: undefined,
      destinationRegion: undefined,
    }))
  }
  const onRoutesClick = () => {
    navigate('/dashboard/procurement/routes')
    clearRoutesFiltersOnComponentChange()
  }

  const onContractsClick = () => {
    navigate('/dashboard/procurement/contracts')
    clearContractFiltersOnComponentChange()
  }
  const onSimClick = () => {
    navigate('/dashboard/procurement/simulation')
  }
  const onCarriersClick = () => {
    navigate(PATHS.PROCUREMENT.CARRIERS)
    clearCarriersFiltersOnComponentChange()
  }
  const onDriversClick = () => {
    navigate(PATHS.PROCUREMENT.DRIVERS)
    clearDriversFiltersOnComponentChange()
  }
  const onVehiclesClick = () => {
    navigate('/dashboard/procurement/Vehicles')
    clearVehiclesFiltersOnComponentChange()
  }
  const onTrailersClick = () => {
    navigate(PATHS.PROCUREMENT.TRAILERS)
    clearTrailersFiltersOnComponentChange()
  }
  const onSuppliersClick = () => {
    navigate(PATHS.PROCUREMENT.SUPPLIERS)
    clearSuppliersFiltersOnComponentChange()
  }
  const onDashboardWeeklyClick = () => {
    navigate(PATHS.PROCUREMENT.DASHBOARD_WEEKLY)
  }
  const onDashboardMonthlyClick = () => {
    navigate(PATHS.PROCUREMENT.DASHBOARD_MONTHLY)
  }

  const items: ItemType<MenuItemType>[] = useMemo(
    () => [
      {
        key: PATHS.PROCUREMENT.ROUTES,
        onClick: onRoutesClick,
        icon: <CompassOutlined style={{ fontSize: '19px' }} />,
        label: t('admin_router.menu.routes'),
      },
      {
        key: 'submen1',
        icon: <SettingOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.settings'),
        children: [
          {
            key: PATHS.PROCUREMENT.SIMULATION,
            onClick: onSimClick,
            label: t('admin_router.menu.simulation'),
          },
        ],
      },
      {
        key: 'submen2',
        icon: <IdcardOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.carriers'),
        children: [
          {
            key: PATHS.PROCUREMENT.CARRIERS,
            onClick: onCarriersClick,
            icon: (
              <span className="submenu-Ident">
                <IdcardOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.carriers'),
          },
          {
            key: PATHS.PROCUREMENT.DRIVERS,
            onClick: onDriversClick,
            icon: (
              <span className="submenu-Ident">
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.drivers'),
          },
          {
            key: PATHS.PROCUREMENT.VEHICLES,
            onClick: onVehiclesClick,
            icon: (
              <span className="submenu-Ident">
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.vehicles'),
          },
          {
            key: PATHS.PROCUREMENT.TRAILERS,
            onClick: onTrailersClick,
            icon: (
              <span className="submenu-Ident">
                <TruckOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.trailers'),
          },
          {
            key: PATHS.PROCUREMENT.CONTRACTS,
            onClick: onContractsClick,
            icon: (
              <span className="submenu-Ident">
                <SolutionOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.contracts')}</span>,
          },
        ],
      },
      {
        key: PATHS.PROCUREMENT.SUPPLIERS,
        onClick: onSuppliersClick,
        icon: <ReconciliationOutlined style={{ fontSize: '18px' }} />,
        label: t('tpl_router.menu.supplier'),
      },
      {
        key: 'procurement-dashboard-metrics',
        icon: <BarChartOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.dashboard'),
        children: [
          {
            key: PATHS.PROCUREMENT.DASHBOARD_WEEKLY,
            onClick: onDashboardWeeklyClick,
            label: t('admin_router.menu.weekly'),
          },
          {
            key: PATHS.PROCUREMENT.DASHBOARD_MONTHLY,
            onClick: onDashboardMonthlyClick,
            label: t('admin_router.menu.monthly'),
          },
        ],
      },
    ],
    [
      onContractsClick,
      onRoutesClick,
      onSimClick,
      onSuppliersClick,
      onTrailersClick,
      onDriversClick,
      onCarriersClick,
      onVehiclesClick,
      onDashboardWeeklyClick,
      onDashboardMonthlyClick,
      pathname,
      t,
    ],
  )

  return <StyledMenu mode="inline" selectedKeys={[pathname]} items={items} />
}

export { ProcurementMenu }
